import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { computeDisplayName, type Contact } from '../../types/contact';
import { listContacts, updateContact } from './api';
import { ContactAvatar } from './ContactAvatar';

// Modal that lets an admin pick an existing Contact and add a new
// AccountLink for the current Account. The new link defaults to:
//   jobTitle: null
//   isPrimary: false
//   receivesServiceCommunications: false
//   receivesBillingCommunications: false
//   receivesCampaignPreviews: false
//
// Admins tune comms toggles + isPrimary via the Contact detail page after
// linking. Contacts already linked to this account are filtered out of the
// picklist (alreadyLinkedIds).

export type LinkExistingContactModalProps = {
  accountId: string;
  alreadyLinkedIds: Set<string>;
  onClose: () => void;
  onLinked: () => void;
};

export function LinkExistingContactModal({
  accountId,
  alreadyLinkedIds,
  onClose,
  onLinked,
}: LinkExistingContactModalProps) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const allContactsQuery = useQuery({ queryKey: ['contacts'], queryFn: () => listContacts() });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const candidates = useMemo<Contact[]>(() => {
    const all = allContactsQuery.data ?? [];
    const q = search.trim().toLowerCase();
    return all
      .filter((c) => !alreadyLinkedIds.has(c.id))
      .filter((c) => {
        if (!q) return true;
        return (
          computeDisplayName(c).toLowerCase().includes(q) ||
          (c.email ?? '').toLowerCase().includes(q) ||
          (c.phone ?? '').toLowerCase().includes(q)
        );
      })
      .slice(0, 100); // cap the DOM size; search narrows further
  }, [allContactsQuery.data, alreadyLinkedIds, search]);

  const linkMutation = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error('No contact selected.');
      const contact = (allContactsQuery.data ?? []).find((c) => c.id === selectedId);
      if (!contact) throw new Error('Selected contact not found in cache.');
      const nextLinks = [
        ...contact.accountLinks,
        {
          accountId,
          jobTitle: null,
          isPrimary: false,
          receivesServiceCommunications: false,
          receivesBillingCommunications: false,
          receivesCampaignPreviews: false,
        },
      ];
      return updateContact(contact.id, { accountLinks: nextLinks });
    },
    onSuccess: () => onLinked(),
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="link-contact-title"
    >
      <div className="w-full max-w-xl rounded-md bg-white shadow-xl">
        <div className="p-6 space-y-4">
          <h2 id="link-contact-title" className="text-lg font-semibold text-slate-900">
            Link an existing contact
          </h2>
          <p className="text-sm text-slate-600">
            Pick a contact already in the system. A new link to this account is added with default
            communication preferences; you can tune them from the contact's detail page.
          </p>

          <input
            type="text"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            placeholder="Search name / email / phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />

          <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md">
            {allContactsQuery.isLoading && <CenteredSpinner />}
            {allContactsQuery.data && candidates.length === 0 && (
              <p className="p-4 text-sm text-slate-500">
                No contacts match. (Already-linked contacts are hidden.)
              </p>
            )}
            {candidates.map((c) => (
              <label
                key={c.id}
                className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer border-b border-slate-100 last:border-b-0 hover:bg-slate-50 ${
                  selectedId === c.id ? 'bg-slate-100' : ''
                }`}
              >
                <input
                  type="radio"
                  name="link-contact-pick"
                  value={c.id}
                  checked={selectedId === c.id}
                  onChange={() => setSelectedId(c.id)}
                />
                <ContactAvatar contact={c} size="sm" decorative />
                <div className="flex-1 min-w-0">
                  <div className="text-slate-900 truncate">{computeDisplayName(c)}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {c.email ?? '—'} · {c.phone ?? '—'}
                  </div>
                </div>
              </label>
            ))}
          </div>

          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={linkMutation.isPending}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                setError(null);
                linkMutation.mutate();
              }}
              disabled={!selectedId || linkMutation.isPending}
            >
              {linkMutation.isPending ? 'Linking...' : 'Link contact'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
